import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import SEO from "@/components/site/SEO";
import Reveal from "@/components/site/Reveal";

const Section = ({ heading, children }) => (
    <div className="mt-10">
        <h2 className="font-serif-editorial text-2xl sm:text-3xl text-brand-primary mb-3 leading-tight">
            {heading}
        </h2>
        <div className="text-brand-text/85 leading-relaxed space-y-4">
            {children}
        </div>
    </div>
);

export default function Complaints() {
    return (
        <>
            <SEO
                title="Complaints Policy | Acumen Financial Services"
                description="How to make a complaint to Acumen Financial Services and HL Partnership Limited, what to expect from our investigation, and how to refer the matter to the Financial Ombudsman Service."
                path="/complaints"
            />

            <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 bg-brand-bg">
                <div className="mx-auto max-w-3xl px-6 md:px-10">
                    <Reveal>
                        <div className="flex items-center gap-3 mb-6">
                            <span className="h-px w-10 bg-brand-accent" />
                            <span className="text-xs uppercase tracking-[0.24em] text-brand-accent font-bold">
                                Complaints policy
                            </span>
                        </div>
                        <h1
                            data-testid="complaints-title"
                            className="font-serif-editorial text-5xl sm:text-6xl tracking-tight text-brand-primary leading-[1.05]"
                        >
                            How to make a complaint.
                        </h1>
                        <p className="mt-6 text-lg text-brand-muted leading-relaxed">
                            Hopefully all your dealings with HL Partnership Limited and
                            our Appointed Representatives will be positive experiences.
                            However, sometimes things go wrong and if that happens, we
                            operate a structured Complaints Procedure to ensure we get
                            things back on track as quickly as possible.
                        </p>
                        <p className="mt-4 text-base text-brand-muted leading-relaxed">
                            This document is a summary of that procedure which is
                            designed to ensure any complaints are dealt with in a
                            professional manner, are handled fairly, effectively, and
                            promptly, and are resolved at the earliest possible
                            opportunity.
                        </p>
                    </Reveal>

                    <Reveal>
                        <Section heading="Our commitment to you">
                            <p>
                                We will endeavour to resolve your complaint as quickly
                                as possible and will work within the following
                                timescales:
                            </p>
                            <ul className="list-disc pl-6 space-y-3">
                                <li>
                                    <strong className="text-brand-primary">
                                        Three Business Days
                                    </strong>{" "}
                                    — Once we receive your complaint, if we are able to
                                    resolve the matter by close of business on the third
                                    working day following its receipt, we will simply do
                                    so and advise you in writing with our Summary
                                    Resolution Communication.
                                </li>
                                <li>
                                    <strong className="text-brand-primary">
                                        Five Business Days
                                    </strong>{" "}
                                    — If we are not able to resolve the matter as above,
                                    we will write to you within five business days
                                    acknowledging your complaint, confirming our
                                    understanding of the situation, and providing you
                                    with the name of the individual handling the
                                    complaint.
                                </li>
                                <li>
                                    <strong className="text-brand-primary">
                                        Four Weeks
                                    </strong>{" "}
                                    — If we haven't been able to resolve the complaint
                                    sooner, after four weeks we will send you either our
                                    Final Response letter (see below) or a progress
                                    report explaining why we are not yet in a position
                                    to resolve the complaint.
                                </li>
                                <li>
                                    <strong className="text-brand-primary">
                                        Eight Weeks
                                    </strong>{" "}
                                    — If we still haven't been able to resolve the case
                                    beforehand, at eight weeks we will send you either
                                    our Final Response letter or an explanation why we
                                    are not yet in a position to resolve the complaint,
                                    giving the reason for the delay and indicating when
                                    we expect to be able to provide a final response.
                                </li>
                            </ul>
                            <p className="text-sm text-brand-muted bg-brand-surface border-l-4 border-brand-accent p-4 rounded">
                                <strong className="text-brand-primary">Please note:</strong>{" "}
                                A business day is a Monday to Friday between 9am and
                                5pm, excluding bank holidays. When a complaint is
                                received on a non-business day, or on a business day
                                outside business hours, we will treat the complaint as
                                being received on the next business day.
                            </p>
                            <p>
                                We will ensure the person dealing with your complaint
                                has the required skill, knowledge, and authority to
                                respond to your complaint. Our staff are fully trained,
                                and we operate a structured and robust complaints
                                procedure to ensure all complaints are dealt with fairly.
                            </p>
                            <p>
                                We will ensure the letters we send are clear and explain
                                things properly. When we resolve a complaint, you will
                                either receive:
                            </p>
                            <ul className="list-disc pl-6 space-y-3">
                                <li>
                                    <strong className="text-brand-primary">
                                        Summary Resolution Communication
                                    </strong>{" "}
                                    — Where we have been able to resolve a complaint
                                    within three business days, we will issue a Summary
                                    Resolution Communication explaining that we believe
                                    the complaint to be resolved. This will include
                                    details of how to escalate the case if you remain
                                    dissatisfied.
                                </li>
                                <li>
                                    <strong className="text-brand-primary">
                                        Final Response Letter
                                    </strong>{" "}
                                    — Once we have completed an investigation, we will
                                    issue our Final Response Letter to you. This will
                                    detail the complaint, explain our investigation, and
                                    confirm our findings. The Final Response letter will
                                    either 'Uphold' or 'Reject' your complaint and
                                    explain why. If there is any settlement or redress
                                    awarded, an explanation of how this was calculated
                                    will also be included.
                                </li>
                            </ul>
                        </Section>
                    </Reveal>

                    <Reveal>
                        <Section heading="The Financial Ombudsman Service">
                            <p>
                                We will give you access to the Financial Ombudsman
                                Service which is a free service set up by Parliament to
                                sort out individual complaints that consumers and
                                financial businesses are not able to resolve themselves.
                            </p>
                            <p>
                                If, once we have issued a Summary Resolution
                                Communication or Final Response Letter (or after 8 weeks
                                if we have yet to issue one), you remain dissatisfied
                                with the outcome of our investigations, you may have the
                                right to approach the Financial Ombudsman Service within
                                6 months of the date of the letter.
                            </p>
                            <p>
                                If you do not refer your complaint in time, the
                                Ombudsman will not have our permission to consider your
                                complaint and so will only be able to do so in very
                                limited circumstances. For example, if the Ombudsman
                                believes that the delay was as a result of exceptional
                                circumstances.
                            </p>
                            <div className="bg-brand-surface border border-brand-border rounded-xl p-6 mt-6">
                                <div className="text-xs uppercase tracking-[0.22em] text-brand-accent font-bold mb-3">
                                    Financial Ombudsman Service
                                </div>
                                <div className="space-y-2 text-sm">
                                    <div className="flex items-start gap-2">
                                        <MapPin size={16} className="text-brand-primary mt-0.5 shrink-0" />
                                        <span>
                                            Exchange Tower, London E14 9SR
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Phone size={16} className="text-brand-primary shrink-0" />
                                        <a
                                            href="tel:03001239123"
                                            className="link-underline text-brand-primary font-semibold"
                                        >
                                            0300 123 9123
                                        </a>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Mail size={16} className="text-brand-primary shrink-0" />
                                        <a
                                            href="https://www.financial-ombudsman.org.uk"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="link-underline text-brand-primary font-semibold"
                                        >
                                            financial-ombudsman.org.uk
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </Section>
                    </Reveal>

                    <Reveal>
                        <Section heading="Your commitment to us">
                            <p>
                                In order to help resolve complaints as quickly and
                                fairly as possible, we ask the following:
                            </p>
                            <ul className="list-disc pl-6 space-y-3">
                                <li>
                                    <strong className="text-brand-primary">
                                        Tell us what happened
                                    </strong>{" "}
                                    — We need to understand the situation as clearly as
                                    possible so we may ask you to provide your side of
                                    the story or let us have documents and information
                                    which may help our investigation. These can be
                                    provided by post, email, verbally or through any
                                    other means that you prefer.
                                </li>
                                <li>
                                    <strong className="text-brand-primary">
                                        Help us find the right solution
                                    </strong>{" "}
                                    — It is in everyone's interests to get complaints
                                    settled amicably so if there is a particular outcome
                                    you believe would be suitable, tell us and we will
                                    assess if this is possible.
                                </li>
                                <li>
                                    <strong className="text-brand-primary">
                                        Respond to us as soon as you can
                                    </strong>{" "}
                                    — We may need further information or clarity on
                                    certain points. If we do need more information, we
                                    simply ask you to come back to us as soon as you
                                    can so we can get things resolved quicker.
                                </li>
                                <li>
                                    <strong className="text-brand-primary">
                                        Treat us with respect
                                    </strong>{" "}
                                    — Our staff will do everything we can to help so
                                    we ask you give them the time to do their work and
                                    treat them with respect.
                                </li>
                            </ul>
                        </Section>
                    </Reveal>

                    <Reveal>
                        <Section heading="How to register a complaint">
                            <p>
                                If you wish to register a complaint, you can do so by
                                contacting our Complaints Department using any of the
                                following methods:
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                                <div className="bg-brand-surface border border-brand-border rounded-xl p-5">
                                    <MapPin size={18} className="text-brand-primary mb-2" />
                                    <div className="text-xs uppercase tracking-[0.18em] font-bold text-brand-muted mb-1">
                                        By post
                                    </div>
                                    <div className="text-sm text-brand-text leading-relaxed">
                                        The Complaints Department<br />
                                        HL Partnership Limited<br />
                                        6 Merus Court, Meridian Business Park<br />
                                        Leicester, LE19 1RJ
                                    </div>
                                </div>
                                <div className="bg-brand-surface border border-brand-border rounded-xl p-5">
                                    <Mail size={18} className="text-brand-primary mb-2" />
                                    <div className="text-xs uppercase tracking-[0.18em] font-bold text-brand-muted mb-1">
                                        By email
                                    </div>
                                    <a
                                        href="mailto:complaints@hlpartnership.co.uk"
                                        className="link-underline text-brand-primary font-semibold text-sm"
                                    >
                                        complaints@hlpartnership.co.uk
                                    </a>
                                </div>
                                <div className="bg-brand-surface border border-brand-border rounded-xl p-5">
                                    <Phone size={18} className="text-brand-primary mb-2" />
                                    <div className="text-xs uppercase tracking-[0.18em] font-bold text-brand-muted mb-1">
                                        By telephone
                                    </div>
                                    <a
                                        href="tel:03300552651"
                                        className="link-underline text-brand-primary font-semibold text-sm"
                                    >
                                        03300 552 651
                                    </a>
                                </div>
                            </div>
                        </Section>
                    </Reveal>

                    <Reveal>
                        <div className="mt-16 pt-8 border-t border-brand-border text-sm text-brand-muted leading-relaxed">
                            <p>
                                Acumen Financial Services Ltd is an appointed
                                representative of HL Partnership Limited, which is
                                authorised and regulated by the Financial Conduct
                                Authority. Acumen Financial Services Ltd (FRN 1001151) ·
                                Company No. SC725584. Registered office: 2/3, 48 George
                                Street, Glasgow, G2 1BP.
                            </p>
                            <p className="mt-3">
                                The guidance and/or information contained within this
                                website is subject to the UK regulatory regime and is
                                therefore targeted at consumers based in the United
                                Kingdom.
                            </p>
                            <p className="mt-6">
                                <Link to="/privacy" className="link-underline text-brand-primary font-semibold">
                                    Read our Privacy Policy →
                                </Link>
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
